import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  UserPlus, Users, Play, CheckCircle2, XCircle, Plus, Trash2, Timer,
  Repeat, Zap, ChevronRight, ChevronDown, AlertTriangle, Radio, Link2, Unlink, Trophy,
  ListOrdered, Crown, Medal, RefreshCw, Volume2, MapPin, Award, ScrollText, Clock,
  Share2, Copy, QrCode, SlidersHorizontal, Pencil, X, Wand2, Gauge, Info, Shuffle, Ban, Layers, Target, Scale, RotateCcw,
} from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import AppHeader from "@/components/AppHeader";
import CasualSync from "@/components/openplay/CasualSync";
import AdBanner from "@/components/AdBanner";
import SessionSetup from "@/components/SessionSetup";
import PlayerNameSearch from "@/components/PlayerNameSearch";
import SessionSwitcher from "@/components/SessionSwitcher";
import CallScreen from "@/components/CallScreen";
import { useAuth } from "@/context/AuthContext";
import { hasCapability } from "@/lib/tournament";
import api from "@/lib/api";
import { announce, primeAudio } from "@/lib/announce";
import AdImage from "@/components/AdImage";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  loadState, saveState, addPlayer, toggleCheckIn, removePlayer, setSettings,
  addCourt, removeCourt, renameCourt, finishMatch, cancelMatch, waitingPool, getPlayer,
  countCheckedIn, countPlaying, restColor, REST_THRESHOLDS, setPartner, clearPartner,
  rotationMeta, endSession, standings, playingPlayerIds, updatePlayer, previewNextMatches, stageLineup,
  SCORING_MODES, metricSummary, pairingNote, editActiveLineup, gamesTargetMeta, arrangeLineup,
  swapWinner, fairnessReport, gamesToRank, checkOutPlayer, countParticipants, hasParticipated,
  listSessions, createSession, switchSession, deleteSession, currentSessionId,
} from "@/lib/openPlayStore";

const REST_STYLE = {
  ready: { color: "var(--rest-ready)", label: "Ready", hex: "#22c55e" },
  warming_up: { color: "#eab308", label: "Warming up", hex: "#eab308" },
  just_finished: { color: "#ef4444", label: "Just finished", hex: "#ef4444" },
};

function mmss(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

const SKILLS = [
  { v: 1, l: "1 · Beginner" }, { v: 2, l: "2 · Improver" }, { v: 3, l: "3 · Intermediate" },
  { v: 4, l: "4 · Advanced" }, { v: 5, l: "5 · Pro" },
];

function MetricsInfo({ mode }) {
  const items = mode === "advanced" ? [
    ["Win Rate (WR)", "Percentage of games won (wins ÷ games played). Primary ranking key."],
    ["PPM Differential", "Points scored minus conceded, per minute of play. Higher = faster, more dominant scoring."],
    ["Score Differential Rate (SDR)", "Average point margin per minute — rewards quick, decisive wins."],
    ["PPM Conceded (PPMC)", "Points allowed per minute. Lower is better (stronger defence)."],
  ] : [
    ["Win Rate (WR)", "Percentage of games won (wins ÷ games played). Primary ranking key."],
    ["Strength of Schedule (SoS)", "Average skill of the opponents faced, shown as a % of the top skill (5.0). Higher = tougher opponents."],
  ];
  const order = mode === "advanced"
    ? "Ranked by: Win Rate → PPM Differential → SDR → PPM Conceded (lowest)."
    : "Ranked by: Win Rate → Strength of Schedule.";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="metrics-info-button" aria-label="What do these metrics mean?" onClick={(e) => e.stopPropagation()}
          className="glass rounded-full w-7 h-7 flex items-center justify-center hover:scale-110 transition-transform shrink-0">
          <Info className="w-4 h-4 text-primary-c" />
        </button>
      </PopoverTrigger>
      <PopoverContent data-testid="metrics-info-content" align="end" className="glass w-80" style={{ background: "var(--surface-glass)" }}>
        <p className="text-xs font-bold text-primary-c uppercase tracking-wider mb-2">Ranking metrics</p>
        <div className="space-y-2.5">
          {items.map(([t, d]) => (
            <div key={t}>
              <div className="text-sm font-bold">{t}</div>
              <div className="text-xs" style={{ color: "var(--text-2)" }}>{d}</div>
            </div>
          ))}
        </div>
        <p className="text-[11px] mt-3 pt-3 border-t" style={{ color: "var(--text-2)", borderColor: "var(--border)" }}>{order}</p>
      </PopoverContent>
    </Popover>
  );
}

function TieInfo({ note, id }) {
  if (!note) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid={`tie-info-${id}`} aria-label="Why this rank?" onClick={(e) => e.stopPropagation()}
          className="rounded-full w-5 h-5 flex items-center justify-center hover:scale-110 transition-transform shrink-0" style={{ background: "rgba(234,179,8,0.15)" }}>
          <Info className="w-3 h-3" style={{ color: "#eab308" }} />
        </button>
      </PopoverTrigger>
      <PopoverContent data-testid={`tie-info-content-${id}`} align="start" className="glass w-72 text-xs" style={{ background: "var(--surface-glass)" }}>
        <p className="font-bold uppercase tracking-wider text-[10px] mb-1" style={{ color: "#eab308" }}>Tie-breaker</p>
        {note}
      </PopoverContent>
    </Popover>
  );
}

function RepeatNote({ note, testid }) {
  const [open, setOpen] = useState(false);
  const lines = note.lines || [{ text: note.reason }];
  return (
    <div data-testid={testid} className="mt-3 rounded-xl px-3 py-2 text-xs" style={{ background: "rgba(234,179,8,0.12)", border: "1px solid rgba(234,179,8,0.4)", color: "#eab308" }}>
      <button type="button" data-testid={`${testid}-toggle`} onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 font-bold text-left">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
        <span className="flex-1">{note.manual ? "Manual override" : `Repeat pairings (${lines.length})`}</span>
        <span className="flex items-center gap-1 font-normal">Details <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} /></span>
      </button>
      {open && <div className="flex flex-col gap-0.5 mt-1.5 pl-5" data-testid={`${testid}-details`}>{lines.map((l, li) => <span key={li}>• {l.text}</span>)}</div>}
    </div>
  );
}


export default function OpenPlay() {
  const { user } = useAuth();
  const audioEnabled = user?.settings?.audio_enabled ?? true;

  const [state, setState] = useState(() => loadState());
  const [now, setNow] = useState(Date.now());
  const [name, setName] = useState("");
  const [skill, setSkill] = useState(3);
  const [warning, setWarning] = useState("");
  const [finishCourt, setFinishCourt] = useState(null);
  const [stopCourt, setStopCourt] = useState(null);
  const [scoreA, setScoreA] = useState("");
  const [scoreB, setScoreB] = useState("");
  const [durationMin, setDurationMin] = useState("");
  const [pairFor, setPairFor] = useState(null);
  const [pairWith, setPairWith] = useState("");
  const [pairSearch, setPairSearch] = useState("");
  const [showStandings, setShowStandings] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [showFairness, setShowFairness] = useState(false);
  const [toRemove, setToRemove] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const [callScreen, setCallScreen] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editSkill, setEditSkill] = useState(3);
  const [editPartner, setEditPartner] = useState("");
  const [overrides, setOverrides] = useState({}); // courtId -> [playerId|null] editable lineup
  const [lobby, setLobby] = useState([]); // extra planned "Up Next" cards (keys) to send to a court
  const [editLineup, setEditLineup] = useState(null); // { courtId } active-match lineup editor
  const [editLineupIds, setEditLineupIds] = useState([]);
  const [editLineupSearch, setEditLineupSearch] = useState("");
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [finalStandings, setFinalStandings] = useState(null);
  const [sessions, setSessions] = useState(() => listSessions());
  const nav = useNavigate();
  const inputRef = useRef(null);
  const pubTimer = useRef(null);

  // capability guard: block Open Play for tiers without the open_play feature
  useEffect(() => {
    if (user && !hasCapability(user, "op_queue")) {
      toast.error("Your plan doesn't include Open Play sessions.", { id: "openplay-cap-guard" });
      nav("/dashboard", { replace: true });
    }
  }, [user, nav]);

  // persist on every change (State Persistence Safety) + keep the session list fresh
  useEffect(() => { saveState(state); setSessions(listSessions()); }, [state]);

  // live clock for timers + rest colors
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const buildSnapshot = () => {
    const playing = playingPlayerIds(state);
    const nm = (id) => getPlayer(state, id)?.name || "";
    const participants = state.players.filter(hasParticipated);
    return {
      event_name: state.session.eventName,
      location: state.session.location,
      mode: state.settings.mode,
      rotation: rotationMeta(state.settings?.rotation).name,
      started_at: state.session?.startedAt || null,
      counts: {
        checkedIn: countCheckedIn(state),
        playing: playing.size,
        participants: participants.length,
        matches: (state.history || []).length,
      },
      participants: participants.map((p) => ({ name: p.name, checked_out: !p.checkedIn && !!p.checkedOut, games: p.gamesPlayed || 0 })),
      courts: state.courts.map((c) => ({
        name: c.name,
        status: c.status,
        startTime: c.startTime,
        teamA: c.currentMatch ? c.currentMatch.teamA.map(nm).filter(Boolean) : [],
        teamB: c.currentMatch ? c.currentMatch.teamB.map(nm).filter(Boolean) : [],
      })),
      queue: waitingPool(state).map((p) => ({ name: p.name, since: p.lastPlayedTimestamp || Date.now() })),
      show_standings: state.settings.liveShowStandings !== false,
      scoring_mode: state.settings?.scoringMode || "casual",
      standings: standings(state, state.settings.pairRankingInclusion !== false)
        .map((p) => {
          const m = metricSummary(p, state.settings?.scoringMode || "casual");
          const needs = gamesToRank(state, p);
          return {
            name: p.name, wins: p.wins || 0, games: p.gamesPlayed, skill: p.skillLevel,
            win_rate: p.winRate || 0, sos: p.strengthOfSchedule || 0,
            ppm_diff: p.avgPPMDiff || 0, sdr: p.avgSDR || 0,
            metric_primary: m.primary, metric_secondary: m.secondary, tag: m.tag,
            provisional: needs > 0, needs, tie_note: p.tieNote || null,
          };
        }),
      history: (state.history || []).map((m) => ({
        court_name: m.court_name, team_a: m.team_a, team_b: m.team_b, winners: m.winners, winner: m.winner,
        duration_seconds: m.duration_seconds, score_a: m.score_a, score_b: m.score_b,
      })),
    };
  };

  // auto-publish public snapshot (debounced) so spectators see it live
  useEffect(() => {
    if (!state.session?.started) return;
    clearTimeout(pubTimer.current);
    pubTimer.current = setTimeout(async () => {
      try {
        await api.post("/openplay/publish", { snapshot: buildSnapshot(), token: state.session?.shareToken, full_state: state });
      } catch (e) { /* offline — spectators keep last snapshot */ }
    }, 1200);
    return () => clearTimeout(pubTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const pool = waitingPool(state);
  const checkedIn = countCheckedIn(state);
  const playing = countPlaying(state);
  const rotation = rotationMeta(state.settings?.rotation);
  const rankIncludePaired = state.settings.pairRankingInclusion !== false;
  const scoringMode = state.settings?.scoringMode || "casual";
  const gtMeta = gamesTargetMeta(state);
  const shareToken = state.session?.shareToken || null;
  const nextPreview = useMemo(() => previewNextMatches(state), [state]);
  const needed = state.settings.mode === "singles" ? 2 : 4;
  const openCourts = state.courts.filter((c) => c.status === "available");

  const PLANNING_KEY = "__next__";
  const planningCourt = { id: PLANNING_KEY, name: "Next Match" };
  const planningBase = () => {
    const avail = waitingPool(state).sort((a, b) => (a.lastPlayedTimestamp || 0) - (b.lastPlayedTimestamp || 0));
    const arr = Array(needed).fill(null);
    for (let k = 0; k < needed && k < avail.length; k++) arr[k] = avail[k].id;
    return arrangeLineup(state, arr, needed);
  };

  const lineupFor = (cid) => {
    if (overrides[cid]) return overrides[cid];
    if (cid === PLANNING_KEY) return planningBase();
    const proj = nextPreview.find((p) => p.courtId === cid);
    if (proj) return [...proj.teamA, ...proj.teamB];
    return Array(needed).fill(null);
  };

  const removeSlot = (cid, idx) => {
    setOverrides((prev) => {
      const cur = [...(prev[cid] || lineupFor(cid))];
      cur[idx] = null;
      return { ...prev, [cid]: cur };
    });
  };

  const autofillCourt = (cid) => {
    const isLobby = String(cid).startsWith("lobby_");
    const cur = [...(overrides[cid] || lineupFor(cid))];
    const assigned = new Set();
    if (isLobby) {
      // Lobby is for planning ahead — draw from ANY waiting player, only avoiding
      // players already placed in OTHER lobby cards (open-court previews are tentative).
      lobby.filter((k) => k !== cid).forEach((k) => (overrides[k] || lineupFor(k)).forEach((id) => id && assigned.add(id)));
    } else {
      [...openCourts.map((c) => c.id), ...lobby].forEach((k) => (overrides[k] || lineupFor(k)).forEach((id) => id && assigned.add(id)));
    }
    const inCur = new Set(cur.filter(Boolean)); // don't reassign players already in this lineup
    const avail = waitingPool(state)
      .filter((p) => !assigned.has(p.id) && !inCur.has(p.id))
      .sort((a, b) => (a.lastPlayedTimestamp || 0) - (b.lastPlayedTimestamp || 0));
    let ai = 0;
    let added = 0;
    for (let i = 0; i < cur.length; i++) {
      if (!cur[i] && ai < avail.length) { cur[i] = avail[ai].id; ai++; added++; }
    }
    if (added === 0) { toast.info(isLobby ? "No waiting players available to plan a match right now." : "No available players to autofill."); return; }
    setOverrides((prev) => ({ ...prev, [cid]: arrangeLineup(state, cur, needed) }));
  };

  // Players a lobby card can still draw from (waiting players not in another lobby card)
  const lobbyAvailableFor = (key) => {
    const taken = new Set();
    lobby.filter((k) => k !== key).forEach((k) => lineupFor(k).forEach((id) => id && taken.add(id)));
    (lineupFor(key) || []).forEach((id) => id && taken.add(id));
    return waitingPool(state).filter((p) => !taken.has(p.id));
  };

  // Put a player in a slot; if they're locked with a partner, seat the partner on the same team.
  const placeWithMate = (arr, idx, playerId, isFree) => {
    const next = [...arr];
    const dup = next.indexOf(playerId);
    if (dup !== -1 && dup !== idx) next[dup] = null;
    next[idx] = playerId;
    const p = getPlayer(state, playerId);
    const mate = p?.partnerId ? getPlayer(state, p.partnerId) : null;
    const half = needed / 2;
    const start = idx < half ? 0 : half;
    const team = Array.from({ length: half }, (_, k) => start + k).filter((k) => k !== idx);
    if (!mate || mate.partnerId !== playerId || half < 2 || team.includes(next.indexOf(mate.id))) return { next, moved: null };
    if (!next.includes(mate.id) && !isFree(mate)) {
      toast.info(`${p.name}'s partner ${mate.name} isn't free right now — added ${p.name} alone.`);
      return { next, moved: null };
    }
    const at = next.indexOf(mate.id);
    if (at !== -1) next[at] = null;
    const slot = team.find((k) => !next[k]) ?? team[0];
    if (at !== -1 && next[slot]) next[at] = next[slot];
    next[slot] = mate.id;
    toast.success(`Partner ${mate.name} added with ${p.name}`);
    return { next, moved: mate.id };
  };

  const fillSlot = (cid, idx, playerId) => {
    const cur = [...(overrides[cid] || lineupFor(cid))];
    const isLobby = String(cid).startsWith("lobby_");
    const cards = isLobby ? lobby : [...(openCourts.length ? openCourts.map((c) => c.id) : [PLANNING_KEY]), ...lobby];
    const waiting = new Set(waitingPool(state).map((x) => x.id));
    const { next, moved } = placeWithMate(cur, idx, playerId, (m) => waiting.has(m.id));
    const out = { ...overrides, [cid]: next };
    if (moved) {
      cards.filter((k) => k !== cid).forEach((k) => {
        const other = overrides[k] || lineupFor(k);
        if (other.includes(moved)) out[k] = other.map((x) => (x === moved ? null : x));
      });
    }
    setOverrides(out);
  };

  // players not already assigned to any open court lineup (or the planning card)
  const assignedAll = new Set();
  if (openCourts.length > 0) {
    openCourts.forEach((c) => lineupFor(c.id).forEach((id) => id && assignedAll.add(id)));
  } else {
    lineupFor(PLANNING_KEY).forEach((id) => id && assignedAll.add(id));
  }
  lobby.forEach((k) => lineupFor(k).forEach((id) => id && assignedAll.add(id)));
  const availablePlayers = waitingPool(state).filter((p) => !assignedAll.has(p.id));

  const stageMessage = (court_name, teamAnames, teamBnames, mode) => {
    const a = teamAnames.join(" and ");
    const b = teamBnames.join(" and ");
    return mode === "singles"
      ? `Now staging on ${court_name}. ${a} versus ${b}`
      : `Now staging on ${court_name}. Team A, ${a}, versus Team B, ${b}`;
  };

  const handleAdd = (e) => {
    e.preventDefault();
    addByName(name, skill);
  };

  const addByName = (nm, sk) => {
    const { state: next, error } = addPlayer(state, nm, sk ?? skill);
    if (error) { toast.error(error); return; }
    setState(next);
    setName("");
    inputRef.current?.focus();
    toast.success("Player added");
  };

  const handleStage = (courtId) => {
    primeAudio();
    const lineup = lineupFor(courtId);
    const res = stageLineup(state, courtId, lineup);
    if (res.error) { setWarning(res.error); return; }
    setState(res.state);
    setOverrides((prev) => { const n = { ...prev }; delete n[courtId]; return n; });
    const { court, teamAnames, teamBnames, mode } = res.staged;
    const call = { court_name: court.name, teamAnames, teamBnames, mode };
    announce(stageMessage(court.name, teamAnames, teamBnames, mode), audioEnabled);
    setCallScreen(call);
    toast.success(`${court.name}: match staged`, {
      description: `${teamAnames.join(" & ")}  vs  ${teamBnames.join(" & ")}`,
    });
  };

  const handleStop = (courtId) => {
    const court = state.courts.find((c) => c.id === courtId);
    if (!court || court.status !== "in_use") { toast.error("No active match on this court."); return; }
    setStopCourt(court);
  };

  const doStop = () => {
    if (!stopCourt) return;
    const res = cancelMatch(state, stopCourt.id);
    const name = stopCourt.name;
    setStopCourt(null);
    if (res.error) { toast.error(res.error); return; }
    setState(res.state);
    toast.info(`${name}: game stopped — no result recorded`);
  };

  // ---- Match Lobby (item 3): pre-build planned line-ups and send to a chosen court
  const addLobby = () => setLobby((prev) => [...prev, `lobby_${Date.now().toString(36)}${prev.length}`]);
  const removeLobby = (key) => {
    setOverrides((prev) => { const n = { ...prev }; delete n[key]; return n; });
    setLobby((prev) => prev.filter((k) => k !== key));
  };
  const sendLobbyToCourt = (key, courtId) => {
    primeAudio();
    const lineup = lineupFor(key);
    const res = stageLineup(state, courtId, lineup);
    if (res.error) { setWarning(res.error); return; }
    setState(res.state);
    removeLobby(key);
    const { court, teamAnames, teamBnames, mode } = res.staged;
    announce(stageMessage(court.name, teamAnames, teamBnames, mode), audioEnabled);
    setCallScreen({ court_name: court.name, teamAnames, teamBnames, mode });
    toast.success(`${court.name}: match staged from lobby`, { description: `${teamAnames.join(" & ")}  vs  ${teamBnames.join(" & ")}` });
  };

  const sendLobbyToNextCourt = (key) => {
    const target = openCourts[0];
    if (!target) { toast.error("No open court available yet."); return; }
    sendLobbyToCourt(key, target.id);
  };

  // ---- Edit an already-started match within the 60s correction window (item 7)
  const openEditLineup = (court) => {
    setEditLineup({ courtId: court.id, name: court.name });
    const ids = [...court.currentMatch.teamA, ...court.currentMatch.teamB];
    while (ids.length < needed) ids.push(null);
    setEditLineupIds(ids.slice(0, needed));
    setEditLineupSearch("");
  };
  const setLineupSlot = (i, id) => {
    const cur = [...editLineupIds];
    while (cur.length < needed) cur.push(null);
    const court = state.courts.find((c) => c.id === editLineup?.courtId);
    const selfIds = [...(court?.currentMatch?.teamA || []), ...(court?.currentMatch?.teamB || [])];
    const busy = playingPlayerIds(state);
    const { next } = placeWithMate(cur, i, id, (m) => m.checkedIn && (!busy.has(m.id) || selfIds.includes(m.id)));
    setEditLineupIds(next);
  };
  const clearLineupSlot = (i) => setEditLineupIds((prev) => {
    const next = [...prev];
    next[i] = null;
    return next;
  });
  const confirmEditLineup = () => {
    if (!editLineup) return;
    const filled = editLineupIds.filter(Boolean);
    if (filled.length !== needed) { toast.error(`A ${state.settings.mode} match needs ${needed} players.`); return; }
    const res = editActiveLineup(state, editLineup.courtId, filled);
    if (res.error) { toast.error(res.error); return; }
    setState(res.state);
    setEditLineup(null);
    toast.success("Lineup updated");
  };

  // shuffle the players currently on a Coming-Up-Next card (randomise pairings)
  const shuffleCourt = (cid) => {
    const cur = [...(overrides[cid] || lineupFor(cid))];
    const filled = cur.filter(Boolean);
    if (filled.length < 2) { toast.info("Add players to the card before shuffling."); return; }
    for (let i = filled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [filled[i], filled[j]] = [filled[j], filled[i]];
    }
    const result = Array(needed).fill(null);
    filled.forEach((id, idx) => { result[idx] = id; });
    setOverrides((prev) => ({ ...prev, [cid]: arrangeLineup(state, result, needed) }));
    toast.success("Line-up shuffled");
  };

  const recallCourt = (court) => {
    if (!court.currentMatch) return;
    primeAudio();
    const teamAnames = court.currentMatch.teamA.map((id) => getPlayer(state, id)?.name).filter(Boolean);
    const teamBnames = court.currentMatch.teamB.map((id) => getPlayer(state, id)?.name).filter(Boolean);
    const mode = state.settings.mode;
    announce(stageMessage(court.name, teamAnames, teamBnames, mode), audioEnabled);
    setCallScreen({ court_name: court.name, teamAnames, teamBnames, mode });
  };

  const handleFinish = (courtId) => {
    const court = state.courts.find((c) => c.id === courtId);
    if (!court || court.status !== "in_use") { toast.error("No active match on this court."); return; }
    const elapsedMin = court.startTime ? Math.max(1, Math.round((Date.now() - court.startTime) / 60000)) : 1;
    setScoreA("");
    setScoreB("");
    setDurationMin(String(elapsedMin));
    setFinishCourt(court);
  };

  const handleSwapWinner = (matchId) => {
    const res = swapWinner(state, matchId);
    if (res.error) { toast.error(res.error); return; }
    setState(res.state);
    toast.success("Winner swapped — standings updated");
  };

  const handleToggleCheckIn = (id) => {
    const res = toggleCheckIn(state, id);
    if (res.error) { toast.error(res.error); return; }
    setState(res.state);
  };

  const confirmRemovePlayer = () => {
    const p = toRemove;
    if (!p) return;
    if (p.checkedIn || p.checkedOut || (p.gamesPlayed || 0) > 0) {
      const res = checkOutPlayer(state, p.id);
      if (res.error) { toast.error(res.error); setToRemove(null); return; }
      setState(res.state);
      toast.success(`${p.name} checked out — kept in the log with their history`);
    } else {
      setState(removePlayer(state, p.id));
      toast.success(`${p.name} removed`);
    }
    setToRemove(null);
  };

  const advancedMode = (state.settings?.scoringMode || "casual") === "advanced";

  const applyResult = async (result) => {
    if (!finishCourt) return;
    const courtId = finishCourt.id;
    const res = finishMatch(state, courtId, result);
    setFinishCourt(null);
    if (res.error) { toast.error(res.error); return; }
    setState(res.state);

    // Carry Plan Over: load the planned "Next Match" line-up onto the court that just freed up
    setOverrides((prev) => {
      const plan = prev[PLANNING_KEY];
      if (!plan || !plan.some(Boolean)) return prev;
      const playing = playingPlayerIds(res.state);
      const cleaned = plan.map((id) => {
        if (!id) return null;
        const p = res.state.players.find((x) => x.id === id);
        return p && p.checkedIn && !playing.has(id) ? id : null;
      });
      if (!cleaned.some(Boolean)) return prev;
      const { [PLANNING_KEY]: _drop, ...rest } = prev;
      toast.info(`Planned line-up loaded onto ${res.finished.court_name}`);
      return { ...rest, [courtId]: cleaned };
    });

    const wl = res.finished.winner === "A" ? res.finished.team_a : res.finished.winner === "B" ? res.finished.team_b : null;
    if (wl && wl.length) {
      primeAudio();
      announce(`${wl.join(" and ")} win on ${res.finished.court_name}`, audioEnabled);
    }
    const desc = res.finished.score_a != null
      ? `${res.finished.team_a.join(" & ")} ${res.finished.score_a}–${res.finished.score_b} ${res.finished.team_b.join(" & ")}`
      : (wl ? `Winner: ${wl.join(" & ")}` : "Result recorded");
    toast.success(`${res.finished.court_name}: match finished`, { description: desc });
    // best-effort cloud sync (online-only)
    try { await api.post("/openplay/match", res.finished); } catch (e) { /* offline */ }
  };

  // Advanced Mode — scores required
  const confirmFinish = async () => {
    const a = Number(scoreA);
    const b = Number(scoreB);
    if (scoreA === "" || scoreB === "" || Number.isNaN(a) || Number.isNaN(b)) {
      toast.error("Enter a final score for both teams.");
      return;
    }
    if (a < 0 || b < 0) { toast.error("Scores cannot be negative."); return; }
    if (a === b) { toast.error("Matches cannot end in a draw."); return; }
    const dur = Math.max(1, Number(durationMin) || 1);
    await applyResult({ scoreA: a, scoreB: b, durationMinutes: dur });
  };

  // Casual Mode — just pick the winning side (no scores needed for WR / SoS)
  const confirmWinner = async (winner) => applyResult({ winner });

  const setBalanced = (balanced) => setState((s) => setSettings(s, { balanced }));

  const confirmEndSession = async () => {
    const finals = standings(state, rankIncludePaired);
    const participants = state.players.filter(hasParticipated);
    const endedAt = Date.now();
    setShowEndConfirm(false);
    try {
      await api.post("/openplay/publish", {
        snapshot: { ...buildSnapshot(), ended: true, ended_at: endedAt },
        token: state.session?.shareToken,
        full_state: state,
        ended: true,
      });
    } catch (e) { /* offline */ }
    setFinalStandings({
      rows: finals,
      mode: scoringMode,
      eventName: state.session?.eventName,
      matches: (state.history || []).length,
      players: participants.length,
      startedAt: state.session?.startedAt || null,
      endedAt,
      participants: participants.map((p) => ({ name: p.name, checkedOut: !p.checkedIn && !!p.checkedOut })),
      history: state.history || [],
    });
    setState(endSession());
  };

  // ---- multi-session controls ----
  const resetTransient = () => {
    setOverrides({}); setLobby([]); setEditId(null); setPairFor(null);
    setFinishCourt(null); setStopCourt(null); setEditLineup(null);
    setShowStandings(false); setShowLog(false); setShowFairness(false); setShowShare(false);
  };
  const handleSwitchSession = (id) => {
    if (id === currentSessionId()) return;
    const ns = switchSession(id);
    resetTransient();
    setState(ns);
  };
  const handleCreateSession = () => {
    const { state: ns } = createSession();
    resetTransient();
    setState(ns);
    toast.success("New session created");
  };
  const handleDeleteSession = (id) => {
    const ns = deleteSession(id);
    resetTransient();
    setState(ns);
    setSessions(listSessions());
    toast.success("Session deleted");
  };
  const sessionSwitcher = (
    <SessionSwitcher sessions={sessions} currentId={currentSessionId()}
      onSwitch={handleSwitchSession} onCreate={handleCreateSession} onDelete={handleDeleteSession} />
  );

  // Session setup gate — configure event before staging courts.
  if (!state.session?.started) {
    return (
      <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }}>
        <AppHeader title="Open-Play" />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 flex justify-end">{sessionSwitcher}</div>
        <SessionSetup state={state} setState={setState} />
        <FinalStandingsModal finals={finalStandings} onClose={() => setFinalStandings(null)} onDashboard={() => { setFinalStandings(null); nav("/dashboard"); }} />
      </div>
    );
  }

  const openEdit = (id) => {
    const p = getPlayer(state, id);
    if (!p) return;
    setEditId(id);
    setEditName(p.name);
    setEditSkill(p.skillLevel);
    setEditPartner(p.partnerId || "");
  };

  const saveEdit = () => {
    const res = updatePlayer(state, editId, { name: editName, skillLevel: editSkill });
    if (res.error) { toast.error(res.error); return; }
    let next = res.state;
    const prevPartner = getPlayer(state, editId)?.partnerId || "";
    if (editPartner !== prevPartner) {
      next = editPartner ? setPartner(next, editId, editPartner) : clearPartner(next, editId);
    }
    setState(next);
    setEditId(null);
    toast.success("Player updated");
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <AppHeader title="Open-Play" />

      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-8 grain">
        {/* command bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="min-w-0">
            <span className="text-xs tracking-[0.25em] uppercase font-bold text-primary-c flex items-center gap-2">
              <Radio className="w-3.5 h-3.5" /> Live Session
            </span>
            <h1 className="font-display font-bold tracking-tight text-3xl mt-1 truncate" data-testid="session-title">{state.session.eventName}</h1>
            <div className="mt-2"><CasualSync state={state} clientId={currentSessionId()} /></div>
            <div className="flex flex-wrap items-center gap-3 mt-1 text-xs" style={{ color: "var(--text-2)" }}>
              {state.session.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {state.session.location}</span>}
              <span className="capitalize">{state.settings.mode}</span>
              <span className="flex items-center gap-1 text-primary-c font-bold" data-testid="rotation-badge"><Repeat className="w-3.5 h-3.5" /> {rotation.name}</span>
              <span className="flex items-center gap-1 font-bold" data-testid="scoring-mode-badge" style={{ color: "var(--text-2)" }}><Gauge className="w-3.5 h-3.5 text-primary-c" />{SCORING_MODES[state.settings?.scoringMode || "casual"].label}</span>
              {gtMeta.target > 0 && (
                <span data-testid="games-target-badge" className="flex items-center gap-1 font-bold rounded-full px-2 py-0.5"
                  style={gtMeta.allReached ? { background: "rgba(34,197,94,0.15)", color: "#22c55e" } : { color: "var(--text-2)" }}>
                  <Target className="w-3.5 h-3.5 text-primary-c" />
                  {gtMeta.allReached ? `Target reached — ${gtMeta.target} games each` : gtMeta.remaining > 0 ? `Target ${gtMeta.target} · ${gtMeta.remaining} game${gtMeta.remaining === 1 ? "" : "s"} to go` : `Target ${gtMeta.target} games per player`}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {sessionSwitcher}
            {state.settings.mode === "doubles" && state.settings.rotation === "open_play" && (
              <div className="glass rounded-full px-4 py-2 flex items-center gap-3 text-sm">
                <Zap className="w-4 h-4 text-primary-c" /> Balanced skill
                <Switch data-testid="balanced-toggle" checked={state.settings.balanced} onCheckedChange={setBalanced} />
              </div>
            )}
            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="live-options-button"
                  className="glass rounded-full px-4 py-2 text-sm font-bold flex items-center gap-2 hover:scale-105 transition-transform">
                  <SlidersHorizontal className="w-4 h-4 text-primary-c" /> Options
                </button>
              </PopoverTrigger>
              <PopoverContent className="glass w-72" style={{ background: "var(--surface-glass)" }} data-testid="live-options-content">
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-semibold text-sm">Standings on live view</div>
                      <div className="text-xs" style={{ color: "var(--text-2)" }}>Show the ranking on the spectator screen.</div>
                    </div>
                    <Switch data-testid="live-standings-toggle"
                      checked={state.settings.liveShowStandings !== false}
                      onCheckedChange={(v) => setState((s) => setSettings(s, { liveShowStandings: v }))} />
                  </div>
                  <div className="h-px" style={{ background: "var(--border)" }} />
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-semibold text-sm">Pair Ranking Inclusion</div>
                      <div className="text-xs" style={{ color: "var(--text-2)" }}>When off, paired players are excluded from the ranking.</div>
                    </div>
                    <Switch data-testid="pair-ranking-toggle"
                      checked={state.settings.pairRankingInclusion !== false}
                      onCheckedChange={(v) => setState((s) => setSettings(s, { pairRankingInclusion: v }))} />
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            <button data-testid="standings-button" onClick={() => setShowStandings(true)}
              className="glass rounded-full px-4 py-2 text-sm font-bold flex items-center gap-2 hover:scale-105 transition-transform">
              <ListOrdered className="w-4 h-4 text-primary-c" /> Standings
            </button>
            <button data-testid="match-log-button" onClick={() => setShowLog(true)}
              className="glass rounded-full px-4 py-2 text-sm font-bold flex items-center gap-2 hover:scale-105 transition-transform">
              <ScrollText className="w-4 h-4 text-primary-c" /> Match Log
              {(state.history?.length || 0) > 0 && (
                <span className="mono text-[10px] rounded-full px-1.5" style={{ background: "var(--primary)", color: "var(--on-primary)" }}>{state.history.length}</span>
              )}
            </button>
            <button data-testid="fairness-button" onClick={() => setShowFairness(true)}
              className="glass rounded-full px-4 py-2 text-sm font-bold flex items-center gap-2 hover:scale-105 transition-transform">
              <Scale className="w-4 h-4 text-primary-c" /> Fairness
            </button>
            <button data-testid="share-button" onClick={() => setShowShare(true)}
              className="btn-glow rounded-full px-4 py-2 text-sm font-bold flex items-center gap-2">
              <Share2 className="w-4 h-4" /> Share & Recap
            </button>
            <button data-testid="end-session-button" onClick={() => setShowEndConfirm(true)}
              className="rounded-full px-4 py-2 text-sm font-bold flex items-center gap-2 hover:scale-105 transition-transform text-white"
              style={{ background: "#dc2626", boxShadow: "0 0 18px rgba(220,38,38,0.45)" }}>
              <XCircle className="w-4 h-4" /> End Session
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* left column: check-in + queue */}
          <div className="lg:col-span-4 space-y-6">
            {/* check-in dashboard */}
            <div className="rounded-2xl glow-border p-6 sm:p-7" style={{ background: "var(--surface)" }}>
              <h3 className="font-display font-bold text-lg flex items-center gap-2 mb-4"><UserPlus className="w-5 h-5 text-primary-c" /> Add &amp; Check-In</h3>
              <form onSubmit={handleAdd} className="space-y-3" data-testid="checkin-form">
                <PlayerNameSearch value={name} onChange={setName} testid="checkin-player-search"
                  inputRef={inputRef} placeholder="Search a past player or add new…"
                  excludeNames={state.players.map((p) => p.name)}
                  onPick={(p) => { setSkill(p.skillLevel); addByName(p.name, p.skillLevel); }}
                  onCreate={(nm) => addByName(nm, skill)} />
                <div className="flex gap-3">
                  <select data-testid="player-skill-select" value={skill} onChange={(e) => setSkill(Number(e.target.value))}
                    className="flex-1 rounded-md bg-surface glow-border px-3 py-3 text-sm outline-none focus:ring-2 focus:ring-lime-400"
                    style={{ color: "var(--text)" }}>
                    {SKILLS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                  </select>
                  <button data-testid="add-player-button" type="submit" className="btn-glow rounded-md px-6 py-3 font-bold flex items-center gap-2">
                    <Plus className="w-4 h-4" /> Add
                  </button>
                </div>
              </form>
              <p className="text-[11px] mt-2" style={{ color: "var(--text-2)" }} data-testid="checkin-hint">Start typing to find players you've run before (with their stats) — pick with ↑ ↓ + Enter or tap, or add a brand-new name. New players are <span className="text-primary-c font-semibold">Pending</span> until you tap <span className="text-primary-c font-semibold">Check in</span>.</p>

              <div className="grid grid-cols-2 gap-3 mt-5">
                <Counter testid="counter-checkedin" label="Checked-in" value={checkedIn} icon={Users} />
                <Counter testid="counter-playing" label="Playing" value={playing} icon={Play} />
              </div>

              {/* roster */}
              {state.players.length > 0 && (
                <div className="mt-5 space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="roster">
                  {state.players.map((p) => (
                    <div key={p.id} className="flex items-center justify-between gap-2 text-sm py-2.5 px-3 rounded-xl" style={{ background: "var(--bg)" }} data-testid={`roster-row-${p.id}`}>
                      <span className="flex items-center gap-2 min-w-0">
                        <span className={`w-2.5 h-2.5 rounded-full shrink-0`} style={{ background: p.checkedIn ? "var(--primary)" : p.checkedOut ? "#eab308" : "var(--text-2)" }} />
                        <button data-testid={`edit-player-${p.id}`} onClick={() => openEdit(p.id)}
                          className="truncate text-left font-medium hover:text-primary-c transition-colors">{p.name}</button>
                        <span className="mono text-xs text-primary-c">L{p.skillLevel}</span>
                        {p.partnerId && getPlayer(state, p.partnerId) && (
                          <span data-testid={`partner-badge-${p.id}`} className="mono text-[10px] px-1.5 py-0.5 rounded glass text-primary-c flex items-center gap-1 shrink-0">
                            <Link2 className="w-3 h-3" />{getPlayer(state, p.partnerId).name}
                          </span>
                        )}
                      </span>
                      <span className="flex items-center gap-1.5 shrink-0">
                        {p.partnerId ? (
                          <button data-testid={`unpair-${p.id}`} onClick={() => setState(clearPartner(state, p.id))}
                            className="text-[10px] font-bold uppercase tracking-wide rounded-full px-2.5 py-1.5 flex items-center gap-1 glass hover:scale-105 transition-transform" style={{ color: "var(--primary-ink)" }}>
                            <Unlink className="w-3.5 h-3.5" /> Unpair
                          </button>
                        ) : (
                          <button data-testid={`pair-${p.id}`} onClick={() => { setPairFor(p.id); setPairWith(""); setPairSearch(""); }}
                            className="text-[10px] font-bold uppercase tracking-wide rounded-full px-2.5 py-1.5 flex items-center gap-1 glass hover:scale-105 transition-transform" style={{ color: "var(--text-2)" }}>
                            <Link2 className="w-3.5 h-3.5" /> Pair
                          </button>
                        )}
                        <button data-testid={`toggle-checkin-${p.id}`} onClick={() => handleToggleCheckIn(p.id)}
                          className="text-[11px] font-bold uppercase tracking-wide rounded-full px-3 py-1.5 flex items-center gap-1 hover:scale-105 transition-transform"
                          style={p.checkedIn ? { background: "rgba(34,197,94,0.16)", color: "#22c55e" } : { background: "var(--primary)", color: "var(--on-primary)" }}>
                          {p.checkedIn ? <><CheckCircle2 className="w-3.5 h-3.5" /> In</> : <><XCircle className="w-3.5 h-3.5" /> {p.checkedOut ? "Back in" : "Check in"}</>}
                        </button>
                        <button data-testid={`remove-player-${p.id}`} onClick={() => setToRemove(p)}
                          aria-label="Remove player" className="p-1.5 hover:scale-110 transition-transform">
                          <Trash2 className="w-4 h-4" style={{ color: "var(--text-2)" }} />
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* waiting queue */}
            <div className="rounded-2xl glow-border p-6" style={{ background: "var(--surface)" }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-bold flex items-center gap-2"><Repeat className="w-5 h-5 text-primary-c" /> Up Next</h3>
                <span className="mono text-xs glass rounded-full px-2.5 py-1" data-testid="queue-count">{pool.length} waiting</span>
              </div>
              {pool.length === 0 ? (
                <p className="text-sm py-4 text-center" style={{ color: "var(--text-2)" }}>Queue is empty. Check players in to get started.</p>
              ) : (
                <div className="space-y-2" data-testid="waiting-queue">
                  <AnimatePresence>
                    {pool.map((p, i) => {
                      const fresh = (p.gamesPlayed || 0) === 0;
                      const rc = restColor(p, now);
                      const st = REST_STYLE[rc];
                      const rest = Math.floor((now - (p.lastPlayedTimestamp || now)) / 1000);
                      const borderHex = fresh ? "var(--primary)" : st.hex;
                      return (
                        <motion.div key={p.id} layout initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}
                          className="flex items-center justify-between rounded-xl px-3 py-2.5" style={{ background: "var(--bg)", borderLeft: `3px solid ${borderHex}` }}
                          data-testid={`queue-item-${i}`}>
                          <span className="flex items-center gap-3 min-w-0">
                            <span className="mono text-xs w-5 text-primary-c">{i + 1}</span>
                            <button data-testid={`edit-queue-${p.id}`} onClick={() => openEdit(p.id)}
                              className="truncate font-medium text-left hover:text-primary-c transition-colors">{p.name}</button>
                          </span>
                          <span className="flex items-center gap-2 shrink-0">
                            {fresh ? (
                              <span className="text-[10px] font-bold uppercase tracking-wider text-primary-c">Ready</span>
                            ) : (
                              <>
                                <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: st.hex }}>{st.label}</span>
                                <span className="mono text-xs" style={{ color: "var(--text-2)" }}>{mmss(rest)}</span>
                              </>
                            )}
                          </span>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </div>
              )}
              <div className="flex gap-3 mt-4 text-[10px] uppercase tracking-wider" style={{ color: "var(--text-2)" }}>
                {Object.values(REST_STYLE).map((s) => (
                  <span key={s.label} className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: s.hex }} />{s.label}</span>
                ))}
              </div>
            </div>
          </div>

          {/* right column: courts */}
          <div className="lg:col-span-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold flex items-center gap-2 text-xl"><Timer className="w-5 h-5 text-primary-c" /> Active Courts</h3>
              <div className="flex gap-2">
                <button data-testid="add-court-button" onClick={() => setState(addCourt(state))}
                  className="glass rounded-full px-4 py-2 text-sm font-bold flex items-center gap-2 hover:scale-105 transition-transform">
                  <Plus className="w-4 h-4 text-primary-c" /> Court
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="courts-grid">
              {state.courts.map((court) => (
                <CourtCard key={court.id} court={court} state={state} now={now}
                  onStage={() => handleStage(court.id)} onFinish={() => handleFinish(court.id)}
                  onRecall={() => recallCourt(court)} onStop={() => handleStop(court.id)}
                  onEdit={() => openEditLineup(court)}
                  onRename={(name) => {
                    const { state: next, error } = renameCourt(state, court.id, name);
                    if (error) { toast.error(error); return false; }
                    setState(next); return true;
                  }}
                  onRemove={() => {
                    const { state: next, error } = removeCourt(state, court.id);
                    if (error) { toast.error(error); return; }
                    setState(next);
                  }} />
              ))}
            </div>

            {/* sponsor ad space */}
            <div className="mt-6">
              <AdBanner />
            </div>

            {/* editable projected next matches (one per open court) */}
            <div className="mt-6" data-testid="next-up-preview">
              <h3 className="font-display font-bold flex items-center gap-2 mb-1 text-xl"><Repeat className="w-5 h-5 text-primary-c" /> Coming Up Next</h3>
              <p className="text-xs mb-4" style={{ color: "var(--text-2)" }}>Projected pairings for your open courts. Remove a player with ✕ to swap them, then Autofill the empty slots.</p>
              {openCourts.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {openCourts.map((court, ci) => {
                    const lineup = lineupFor(court.id);
                    const half = needed / 2;
                    return (
                      <div key={court.id} data-testid={`next-up-${ci}`} className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }}>
                        <div className="flex items-center justify-between mb-4">
                          <span className="font-display font-bold text-lg">{court.name}</span>
                          <div className="flex items-center gap-2">
                            <button data-testid={`nextup-shuffle-${ci}`} onClick={() => shuffleCourt(court.id)}
                              className="glass rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 hover:scale-105 transition-transform">
                              <Shuffle className="w-3.5 h-3.5 text-primary-c" /> Shuffle
                            </button>
                            <button data-testid={`nextup-autofill-${ci}`} onClick={() => autofillCourt(court.id)}
                              className="glass rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 hover:scale-105 transition-transform">
                              <Wand2 className="w-3.5 h-3.5 text-primary-c" /> Autofill
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-3 items-center">
                          <NextTeam court={court} ids={lineup.slice(0, half)} offset={0} state={state} onRemove={removeSlot} available={availablePlayers} onFill={fillSlot} />
                          <span className="mono text-sm font-bold" style={{ color: "var(--text-2)" }}>VS</span>
                          <NextTeam court={court} ids={lineup.slice(half, needed)} offset={half} state={state} onRemove={removeSlot} available={availablePlayers} onFill={fillSlot} />
                        </div>
                        {(() => {
                          const note = pairingNote(state, lineup.slice(0, half), lineup.slice(half, needed), !!overrides[court.id]);
                          return note?.repeat ? <RepeatNote note={note} testid={`repeat-pairing-badge-${ci}`} /> : null;
                        })()}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div data-testid="next-up-empty" data-planning="true" className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-display font-bold text-lg flex items-center gap-2"><Timer className="w-4 h-4 text-primary-c" /> Next Match</span>
                    <div className="flex items-center gap-2">
                      <button data-testid="nextup-shuffle-planning" onClick={() => shuffleCourt(PLANNING_KEY)}
                        className="glass rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 hover:scale-105 transition-transform">
                        <Shuffle className="w-3.5 h-3.5 text-primary-c" /> Shuffle
                      </button>
                      <button data-testid="nextup-autofill-planning" onClick={() => autofillCourt(PLANNING_KEY)}
                        className="glass rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 hover:scale-105 transition-transform">
                        <Wand2 className="w-3.5 h-3.5 text-primary-c" /> Autofill
                      </button>
                    </div>
                  </div>
                  <p className="text-xs mb-4" style={{ color: "var(--text-2)" }}>All courts are in play — plan the next line-up now. Empty slots fill automatically as players finish and free up.</p>
                  {(() => {
                    const lineup = lineupFor(PLANNING_KEY);
                    const half = needed / 2;
                    const note = pairingNote(state, lineup.slice(0, half), lineup.slice(half, needed), !!overrides[PLANNING_KEY]);
                    return (
                      <>
                        <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-3 items-center">
                          <NextTeam court={planningCourt} ids={lineup.slice(0, half)} offset={0} state={state} onRemove={removeSlot} available={availablePlayers} onFill={fillSlot} />
                          <span className="mono text-sm font-bold" style={{ color: "var(--text-2)" }}>VS</span>
                          <NextTeam court={planningCourt} ids={lineup.slice(half, needed)} offset={half} state={state} onRemove={removeSlot} available={availablePlayers} onFill={fillSlot} />
                        </div>
                        {note?.repeat && <RepeatNote note={note} testid="repeat-pairing-badge-planning" />}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Up Next Queue: one shared ordered queue — the top line-up drops onto the next court that frees up */}
            <div className="mt-6" data-testid="match-lobby">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-display font-bold flex items-center gap-2 text-xl"><Layers className="w-5 h-5 text-primary-c" /> Up Next Queue</h3>
                <button data-testid="lobby-add" onClick={addLobby}
                  className="glass rounded-full px-4 py-2 text-sm font-bold flex items-center gap-2 hover:scale-105 transition-transform">
                  <Plus className="w-4 h-4 text-primary-c" /> Add line-up
                </button>
              </div>
              <p className="text-xs mb-4" style={{ color: "var(--text-2)" }}>An ordered queue that isn't tied to any court — the top line-up drops onto whichever court frees up next, then the rest shift up.</p>
              {lobby.length === 0 ? (
                <p className="text-sm rounded-2xl glow-border p-5 text-center" style={{ background: "var(--surface)", color: "var(--text-2)" }} data-testid="lobby-empty">
                  No line-ups queued yet. Tap “Add line-up” to queue one.
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="lobby-list">
                  {lobby.map((key, li) => {
                    const lineup = lineupFor(key);
                    const half = needed / 2;
                    const filled = lineup.filter(Boolean).length === needed;
                    const isNext = li === 0;
                    return (
                      <div key={key} data-testid={`lobby-card-${li}`} className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)", borderColor: isNext ? "var(--primary)" : undefined, boxShadow: isNext ? "0 0 16px rgba(0,229,255,0.18)" : "none" }}>
                        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                          <span className="font-display font-bold text-lg flex items-center gap-2 whitespace-nowrap">
                            {isNext && <span className="mono text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 whitespace-nowrap" style={{ background: "var(--primary)", color: "var(--on-primary)" }}>Up Next</span>}
                            {isNext ? "Next line-up" : `In queue #${li + 1}`}
                          </span>
                          <div className="flex items-center gap-2">
                            <button data-testid={`lobby-shuffle-${li}`} onClick={() => shuffleCourt(key)}
                              className="glass rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 hover:scale-105 transition-transform">
                              <Shuffle className="w-3.5 h-3.5 text-primary-c" /> Shuffle
                            </button>
                            <button data-testid={`lobby-autofill-${li}`} onClick={() => autofillCourt(key)}
                              className="glass rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 hover:scale-105 transition-transform">
                              <Wand2 className="w-3.5 h-3.5 text-primary-c" /> Autofill
                            </button>
                            <button data-testid={`lobby-remove-${li}`} onClick={() => removeLobby(key)} aria-label="Remove line-up"
                              className="glass rounded-full w-8 h-8 flex items-center justify-center hover:scale-105 transition-transform">
                              <Trash2 className="w-3.5 h-3.5" style={{ color: "var(--text-2)" }} />
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-3 items-center">
                          <NextTeam court={{ id: key, name: `Line-up #${li + 1}` }} ids={lineup.slice(0, half)} offset={0} state={state} onRemove={removeSlot} available={lobbyAvailableFor(key)} onFill={fillSlot} />
                          <span className="mono text-sm font-bold" style={{ color: "var(--text-2)" }}>VS</span>
                          <NextTeam court={{ id: key, name: `Line-up #${li + 1}` }} ids={lineup.slice(half, needed)} offset={half} state={state} onRemove={removeSlot} available={lobbyAvailableFor(key)} onFill={fillSlot} />
                        </div>
                        <div className="mt-4">
                          <button data-testid={`lobby-send-next-${li}`} onClick={() => sendLobbyToNextCourt(key)}
                            disabled={!filled || openCourts.length === 0}
                            className="btn-glow w-full rounded-full px-4 py-2.5 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50">
                            <ChevronRight className="w-4 h-4" />
                            {openCourts.length === 0 ? "No open court yet" : !filled ? "Fill all slots first" : `Send to ${openCourts[0].name}`}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* insufficient players / edge-case warning modal */}
      <Dialog open={!!warning} onOpenChange={(o) => !o && setWarning("")}>
        <DialogContent data-testid="warning-modal" className="glass" style={{ background: "var(--surface-glass)" }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <AlertTriangle className="w-5 h-5" style={{ color: "#eab308" }} /> Cannot stage match
            </DialogTitle>
            <DialogDescription style={{ color: "var(--text-2)" }}>{warning}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button data-testid="warning-modal-close" onClick={() => setWarning("")} className="btn-glow rounded-full px-5 py-2 font-bold">Got it</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* finish match modal — winner pick (casual) or score entry (advanced) */}
      <Dialog open={!!finishCourt} onOpenChange={(o) => !o && setFinishCourt(null)}>
        <DialogContent data-testid="score-modal" className="glass" style={{ background: "var(--surface-glass)" }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Trophy className="w-5 h-5 text-primary-c" /> {advancedMode ? `Final score on ${finishCourt?.name}` : `Who won on ${finishCourt?.name}?`}
            </DialogTitle>
            <DialogDescription style={{ color: "var(--text-2)" }}>
              {advancedMode
                ? "Enter each team's final score. Match duration is auto-filled from the court timer — edit if needed."
                : "Tap the winning side. Casual mode tracks win rate & strength of schedule — no scores needed."}
            </DialogDescription>
          </DialogHeader>

          {finishCourt?.currentMatch && !advancedMode && (
            <div className="grid grid-cols-2 gap-3">
              {[["A", "teamA"], ["B", "teamB"]].map(([w, key]) => (
                <button key={w} data-testid={`winner-team-${w.toLowerCase()}`} onClick={() => confirmWinner(w)}
                  className="rounded-xl glow-border p-4 text-left hover:scale-[1.02] transition-transform" style={{ background: "var(--surface)" }}>
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-primary-c mb-2 flex items-center gap-1">
                    <Trophy className="w-3 h-3" /> Team {w} wins
                  </div>
                  {finishCourt.currentMatch[key].map((id) => (
                    <div key={id} className="text-sm font-medium truncate">{getPlayer(state, id)?.name || "—"}</div>
                  ))}
                </button>
              ))}
            </div>
          )}

          {finishCourt?.currentMatch && advancedMode && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {[["A", "teamA", scoreA, setScoreA], ["B", "teamB", scoreB, setScoreB]].map(([w, key, val, setVal]) => (
                  <div key={w} className="rounded-xl glow-border p-4" style={{ background: "var(--surface)" }}>
                    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-primary-c mb-2">Team {w}</div>
                    {finishCourt.currentMatch[key].map((id) => (
                      <div key={id} className="text-sm font-medium truncate">{getPlayer(state, id)?.name || "—"}</div>
                    ))}
                    <input data-testid={`score-input-${w.toLowerCase()}`} type="text" inputMode="numeric" pattern="[0-9]*" maxLength={3}
                      value={val} onChange={(e) => setVal(e.target.value.replace(/[^0-9]/g, ""))}
                      placeholder={String(state.settings?.winningScoreThreshold || 11)}
                      className="mt-3 w-full rounded-md bg-surface glow-border px-3 py-2 text-lg font-bold mono outline-none focus:ring-2 focus:ring-lime-400"
                      style={{ color: "var(--text)" }} />
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <label className="text-sm flex items-center gap-1.5" style={{ color: "var(--text-2)" }}>
                  <Clock className="w-4 h-4 text-primary-c" /> Duration
                </label>
                <input data-testid="score-duration" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={4} value={durationMin}
                  onChange={(e) => setDurationMin(e.target.value.replace(/[^0-9]/g, ""))}
                  className="w-24 rounded-md bg-surface glow-border px-3 py-2 text-sm mono outline-none focus:ring-2 focus:ring-lime-400"
                  style={{ color: "var(--text)" }} />
                <span className="text-xs mono" style={{ color: "var(--text-2)" }}>minutes (from timer)</span>
              </div>
            </div>
          )}

          <DialogFooter>
            <button data-testid="score-cancel" onClick={() => setFinishCourt(null)}
              className="glass rounded-full px-4 py-2 font-bold" style={{ color: "var(--text)" }}>Cancel</button>
            {advancedMode && (
              <button data-testid="score-submit" onClick={confirmFinish}
                className="btn-glow rounded-full px-5 py-2 font-bold flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> Record Result
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* stop game confirmation (no bearing on standings) */}
      <Dialog open={!!stopCourt} onOpenChange={(o) => !o && setStopCourt(null)}>
        <DialogContent data-testid="stop-modal" className="glass" style={{ background: "var(--surface-glass)" }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Ban className="w-5 h-5 text-primary-c" /> Stop the game on {stopCourt?.name}?
            </DialogTitle>
            <DialogDescription style={{ color: "var(--text-2)" }}>
              This ends the current match with <strong>no result recorded</strong> — no wins, losses or metrics change. The players return to the queue.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button data-testid="stop-cancel" onClick={() => setStopCourt(null)}
              className="glass rounded-full px-4 py-2 font-bold" style={{ color: "var(--text)" }}>Keep playing</button>
            <button data-testid="stop-confirm" onClick={doStop}
              className="rounded-full px-5 py-2 font-bold flex items-center gap-2" style={{ background: "#ef4444", color: "white" }}>
              <Ban className="w-4 h-4" /> Stop game
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* player pairing modal */}
      <Dialog open={!!pairFor} onOpenChange={(o) => !o && setPairFor(null)}>
        <DialogContent data-testid="pair-modal" className="glass" style={{ background: "var(--surface-glass)" }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Link2 className="w-5 h-5 text-primary-c" /> Pair {getPlayer(state, pairFor)?.name}
            </DialogTitle>
            <DialogDescription style={{ color: "var(--text-2)" }}>
              Paired players always share a team until unpaired.
            </DialogDescription>
          </DialogHeader>
          <input data-testid="pair-search" value={pairSearch} onChange={(e) => setPairSearch(e.target.value)} autoFocus
            placeholder="Search a player by name…"
            className="w-full rounded-md bg-surface glow-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-lime-400 mb-2"
            style={{ color: "var(--text)" }} />
          <div data-testid="pair-list" className="max-h-64 overflow-y-auto rounded-md glow-border divide-y" style={{ borderColor: "var(--border)" }}>
            {state.players
              .filter((x) => x.id !== pairFor && !x.partnerId && x.name.toLowerCase().includes(pairSearch.trim().toLowerCase()))
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((x) => (
                <button key={x.id} data-testid={`pair-option-${x.id}`} onClick={() => setPairWith(x.id)}
                  className={`w-full text-left px-3 py-2.5 text-sm flex items-center justify-between transition-colors ${pairWith === x.id ? "btn-glow" : "hover:bg-[var(--surface)]"}`}
                  style={pairWith === x.id ? {} : { color: "var(--text)" }}>
                  <span>{x.name}</span>
                  <span className="mono text-xs text-primary-c">L{x.skillLevel}</span>
                </button>
              ))}
            {state.players.filter((x) => x.id !== pairFor && !x.partnerId && x.name.toLowerCase().includes(pairSearch.trim().toLowerCase())).length === 0 && (
              <p className="px-3 py-4 text-sm text-center" style={{ color: "var(--text-2)" }}>No available players match.</p>
            )}
          </div>
          <DialogFooter>
            <button data-testid="pair-cancel" onClick={() => setPairFor(null)}
              className="glass rounded-full px-4 py-2 font-bold" style={{ color: "var(--text)" }}>Cancel</button>
            <button data-testid="pair-confirm" disabled={!pairWith}
              onClick={() => { setState(setPartner(state, pairFor, pairWith)); setPairFor(null); toast.success("Players paired"); }}
              className="btn-glow rounded-full px-5 py-2 font-bold disabled:opacity-50">Pair</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* edit active-match lineup (60s correction window) */}
      <Dialog open={!!editLineup} onOpenChange={(o) => !o && setEditLineup(null)}>
        <DialogContent data-testid="edit-lineup-modal" className="glass" style={{ background: "var(--surface-glass)" }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Pencil className="w-5 h-5 text-primary-c" /> Edit lineup · {editLineup?.name}
            </DialogTitle>
            <DialogDescription style={{ color: "var(--text-2)" }}>
              Tap ✕ to remove a player, then pick a replacement from the available list. First {needed / 2} form Team A.
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs font-bold mb-2" style={{ color: editLineupIds.filter(Boolean).length === needed ? "#22c55e" : "#eab308" }} data-testid="edit-lineup-count">
            {editLineupIds.filter(Boolean).length} / {needed} selected
          </div>
          {(() => {
            const selfIds = editLineup ? [...(state.courts.find((c) => c.id === editLineup.courtId)?.currentMatch?.teamA || []), ...(state.courts.find((c) => c.id === editLineup.courtId)?.currentMatch?.teamB || [])] : [];
            const busyElsewhere = playingPlayerIds(state);
            const half = needed / 2;
            const slotEls = Array.from({ length: needed }).map((_, i) => {
              const id = editLineupIds[i];
              const p = id ? getPlayer(state, id) : null;
              const opts = state.players
                .filter((x) => x.checkedIn && (!busyElsewhere.has(x.id) || selfIds.includes(x.id)))
                .filter((x) => !editLineupIds.includes(x.id))
                .sort((a, b) => a.name.localeCompare(b.name));
              return (
                <div key={i} data-testid={`edit-lineup-slot-${i}`} className="rounded-xl glow-border px-3 py-2.5 flex items-center gap-2" style={{ background: "var(--surface)" }}>
                  <span className="mono text-[10px] px-1.5 py-0.5 rounded-full shrink-0" style={{ background: "var(--bg)", color: "var(--primary-ink)" }}>{i < half ? "A" : "B"}</span>
                  {p ? (
                    <>
                      <span className="font-semibold text-sm truncate flex-1">{p.name} <span className="mono text-xs text-primary-c">L{p.skillLevel}</span></span>
                      <button data-testid={`edit-lineup-remove-${i}`} onClick={() => clearLineupSlot(i)} aria-label="Remove player"
                        className="w-7 h-7 rounded-full glass flex items-center justify-center shrink-0 hover:scale-105 transition-transform">
                        <X className="w-3.5 h-3.5" style={{ color: "#ef4444" }} />
                      </button>
                    </>
                  ) : (
                    <select data-testid={`edit-lineup-select-${i}`} defaultValue="" onChange={(e) => e.target.value && setLineupSlot(i, e.target.value)}
                      className="flex-1 rounded-md bg-bg glow-border px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-lime-400" style={{ color: "var(--text)", background: "var(--bg)" }}>
                      <option value="">{opts.length ? "Add player…" : "No available players"}</option>
                      {opts.map((x) => <option key={x.id} value={x.id}>{`${x.name} (L${x.skillLevel})`}</option>)}
                    </select>
                  )}
                </div>
              );
            });
            return (
              <div className="space-y-2" data-testid="edit-lineup-slots">
                <div className="grid grid-cols-2 gap-2">{slotEls.slice(0, half)}</div>
                <div className="text-center mono text-xs font-bold" style={{ color: "var(--text-2)" }}>VS</div>
                <div className="grid grid-cols-2 gap-2">{slotEls.slice(half)}</div>
              </div>
            );
          })()}
          <DialogFooter>
            <button data-testid="edit-lineup-cancel" onClick={() => setEditLineup(null)}
              className="glass rounded-full px-4 py-2 font-bold" style={{ color: "var(--text)" }}>Cancel</button>
            <button data-testid="edit-lineup-confirm" disabled={editLineupIds.filter(Boolean).length !== needed} onClick={confirmEditLineup}
              className="btn-glow rounded-full px-5 py-2 font-bold disabled:opacity-50">Update lineup</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* current standings modal */}
      <Dialog open={showStandings} onOpenChange={setShowStandings}>
        <DialogContent data-testid="standings-modal" className="glass max-w-lg" style={{ background: "var(--surface-glass)" }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <ListOrdered className="w-5 h-5 text-primary-c" /> Current Standings
              <span className="ml-auto"><MetricsInfo mode={scoringMode} /></span>
            </DialogTitle>
            <DialogDescription style={{ color: "var(--text-2)" }}>
              {scoringMode === "advanced" ? "Ranked by win rate, PPM differential & score-differential rate." : "Ranked by win rate & strength of schedule."}
            </DialogDescription>
          </DialogHeader>
          {state.players.length === 0 ? (
            <p className="text-sm py-6 text-center" style={{ color: "var(--text-2)" }}>No players yet.</p>
          ) : (
            <div className="space-y-1.5 max-h-[55vh] overflow-y-auto pr-1" data-testid="standings-list">
              {(() => {
                const min = Number(state.settings?.minGamesToRank) || 0;
                const anyRanked = state.players.some((p) => (p.gamesPlayed || 0) >= min);
                let rankCounter = 0;
                return (
                  <>
                    {min > 0 && !anyRanked && (
                      <p data-testid="standings-no-ranked-hint" className="text-xs text-center py-2 rounded-lg mb-1" style={{ background: "rgba(234,179,8,0.1)", color: "#eab308" }}>
                        No ranked players yet — {min} games required. Standings below are provisional.
                      </p>
                    )}
                    {standings(state, rankIncludePaired).map((p) => {
                const need = gamesToRank(state, p);
                const provisional = need > 0;
                const rank = provisional ? null : ++rankCounter;
                const podium = rank ? PODIUM[rank] : null;
                const m = metricSummary(p, scoringMode);
                return (
                  <div key={p.id} data-testid={provisional ? `standing-provisional-${p.id}` : `standing-row-${rank}`}
                    className="flex items-center justify-between rounded-xl px-3 py-2.5"
                    style={{
                      background: podium ? podium.bg : "var(--bg)",
                      border: podium ? `1px solid ${podium.color}` : "1px solid transparent",
                      opacity: provisional ? 0.55 : 1,
                    }}>
                    <span className="flex items-center gap-3 min-w-0">
                      <span className="w-7 flex items-center justify-center shrink-0">
                        {podium
                          ? <podium.icon className="w-4 h-4" style={{ color: podium.color }} />
                          : <span className="mono text-xs" style={{ color: "var(--text-2)" }}>{rank || "—"}</span>}
                      </span>
                      <span className="min-w-0">
                        <span className="truncate font-semibold flex items-center gap-2" style={podium ? { color: podium.color } : {}}>
                          {p.name}
                          {!provisional && <TieInfo note={p.tieNote} id={`standing-${rank}`} />}
                          {m.tag && !provisional && <span className="mono text-[9px] uppercase tracking-wider glass rounded-full px-1.5 py-0.5 text-primary-c shrink-0">{m.tag}</span>}
                          {provisional && <span data-testid={`needs-games-${p.id}`} className="mono text-[9px] uppercase tracking-wider rounded-full px-1.5 py-0.5 shrink-0" style={{ background: "rgba(234,179,8,0.16)", color: "#eab308" }}>needs {need} more game{need === 1 ? "" : "s"}</span>}
                        </span>
                        <span className="mono text-[11px]" style={{ color: "var(--text-2)" }} data-testid={`standing-metrics-${rank || p.id}`}>{m.primary} · {m.secondary}</span>
                      </span>
                    </span>
                    <span className="flex items-center gap-4 mono text-sm shrink-0">
                      <span className="font-bold" style={podium ? { color: podium.color } : { color: "var(--text)" }}>{p.wins || 0}W</span>
                      <span style={{ color: "var(--text-2)" }}>{p.gamesPlayed}g</span>
                    </span>
                  </div>
                );
              })}
                  </>
                );
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* match log */}
      <Dialog open={showLog} onOpenChange={setShowLog}>
        <DialogContent data-testid="match-log-modal" className="glass max-w-lg" style={{ background: "var(--surface-glass)" }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <ScrollText className="w-5 h-5 text-primary-c" /> Match Log
            </DialogTitle>
            <DialogDescription style={{ color: "var(--text-2)" }}>Completed matches and their winners, newest first.</DialogDescription>
          </DialogHeader>
          {(state.history?.length || 0) === 0 ? (
            <p className="text-sm py-6 text-center" style={{ color: "var(--text-2)" }}>No matches finished yet.</p>
          ) : (
            <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1" data-testid="match-log-list">
              {state.history.map((m, i) => {
                const aWon = m.winner === "A";
                const bWon = m.winner === "B";
                return (
                  <div key={m.id} data-testid={`match-log-row-${i}`} className="rounded-xl px-3 py-2.5" style={{ background: "var(--bg)" }}>
                    <div className="flex items-center justify-between text-[10px] tracking-[0.2em] uppercase font-bold mb-2" style={{ color: "var(--text-2)" }}>
                      <span className="text-primary-c">{m.court_name} · {m.mode}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {mmss(m.duration_seconds)}</span>
                    </div>
                    <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 text-sm">
                      <span className={`truncate ${aWon ? "font-bold text-primary-c" : ""}`}>
                        {aWon && <Trophy className="w-3.5 h-3.5 inline mr-1" />}{m.team_a.join(" & ")}
                      </span>
                      <span className="mono text-xs shrink-0" style={{ color: "var(--text-2)" }}>
                        {Number.isFinite(m.score_a) && Number.isFinite(m.score_b) ? `${m.score_a} – ${m.score_b}` : "vs"}
                      </span>
                      <span className={`truncate text-right ${bWon ? "font-bold text-primary-c" : ""}`}>
                        {m.team_b.join(" & ")}{bWon && <Trophy className="w-3.5 h-3.5 inline ml-1" />}
                      </span>
                    </div>
                    <div className="mt-1.5 flex items-center justify-between gap-2">
                      <span className="text-[11px]" style={{ color: "var(--text-2)" }}>
                        {m.winners?.length ? `Winner: ${m.winners.join(" & ")}` : "No result recorded"}
                      </span>
                      {(m.winner === "A" || m.winner === "B") && (
                        <button data-testid={`swap-winner-${i}`} onClick={() => handleSwapWinner(m.id)}
                          className="glass rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide flex items-center gap-1 hover:scale-105 transition-transform shrink-0" style={{ color: "var(--text)" }}>
                          <RotateCcw className="w-3 h-3 text-primary-c" /> Swap winner
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* remove / check-out player confirmation */}
      <AlertDialog open={!!toRemove} onOpenChange={(o) => !o && setToRemove(null)}>
        <AlertDialogContent className="glass" style={{ background: "var(--surface-glass)" }} data-testid="remove-player-dialog">
          {(() => {
            const retain = toRemove && (toRemove.checkedIn || toRemove.checkedOut || (toRemove.gamesPlayed || 0) > 0);
            return (
              <>
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-display flex items-center gap-2">
                    <Trash2 className="w-5 h-5" style={{ color: retain ? "#eab308" : "#ef4444" }} />
                    {retain ? `Check out ${toRemove?.name}?` : `Remove ${toRemove?.name}?`}
                  </AlertDialogTitle>
                  <AlertDialogDescription style={{ color: "var(--text-2)" }}>
                    {retain
                      ? "They'll move to the checked-out log and keep their match history and ranking. You can check them back in anytime."
                      : "This player hasn't checked in yet, so they'll be removed from the roster completely. This can't be undone."}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="remove-player-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction data-testid="remove-player-confirm" onClick={(e) => { e.preventDefault(); confirmRemovePlayer(); }}
                    className={retain ? "bg-yellow-500 hover:bg-yellow-600 text-black" : "bg-red-600 hover:bg-red-700 text-white"}>
                    {retain ? "Check out" : "Remove"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </>
            );
          })()}
        </AlertDialogContent>
      </AlertDialog>

      {/* fairness dashboard */}
      <Dialog open={showFairness} onOpenChange={setShowFairness}>
        <DialogContent data-testid="fairness-modal" className="glass max-w-lg" style={{ background: "var(--surface-glass)" }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Scale className="w-5 h-5 text-primary-c" /> Fairness Dashboard
            </DialogTitle>
            <DialogDescription style={{ color: "var(--text-2)" }}>
              How evenly partners &amp; opponents have been shared across the session. 100% = never repeated.
            </DialogDescription>
          </DialogHeader>
          {(() => {
            const report = fairnessReport(state);
            const played = report.rows.filter((r) => r.totalPartnerings > 0 || r.totalOpponentings > 0);
            if (played.length === 0) {
              return <p className="text-sm py-6 text-center" style={{ color: "var(--text-2)" }}>Play a few matches to see how fairly the rotation is spreading partners and opponents.</p>;
            }
            const pct = (x) => `${Math.round((x || 0) * 100)}%`;
            return (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2" data-testid="fairness-overall">
                  {[
                    { label: "Overall", v: pct(report.overall.score) },
                    { label: "Partner mix", v: pct(report.overall.partnerDiversity) },
                    { label: "Opponent mix", v: pct(report.overall.opponentDiversity) },
                  ].map((c) => (
                    <div key={c.label} className="rounded-xl px-3 py-2.5 text-center" style={{ background: "var(--bg)" }}>
                      <div className="font-display font-bold text-lg text-primary-c">{c.v}</div>
                      <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-2)" }}>{c.label}</div>
                    </div>
                  ))}
                </div>
                <p className="text-[11px]" style={{ color: "var(--text-2)" }} data-testid="fairness-repeats">
                  {report.overall.totalRepeatPartners} repeat partnership{report.overall.totalRepeatPartners === 1 ? "" : "s"} · {report.overall.totalRepeatOpponents} repeat opponent match-up{report.overall.totalRepeatOpponents === 1 ? "" : "s"} so far. Players who need attention are listed first.
                </p>
                <div className="space-y-1.5 max-h-[45vh] overflow-y-auto pr-1" data-testid="fairness-list">
                  {played.map((r) => (
                    <div key={r.id} data-testid={`fairness-row-${r.id}`} className="rounded-xl px-3 py-2.5" style={{ background: "var(--bg)" }}>
                      <div className="flex items-center justify-between gap-2 text-sm">
                        <span className="font-semibold truncate">{r.name}</span>
                        <span className="mono text-xs" style={{ color: "var(--text-2)" }}>{r.gamesPlayed}g</span>
                      </div>
                      <div className="mt-1.5 grid grid-cols-2 gap-3">
                        {[
                          { k: "Partners", uniq: r.uniquePartners, tot: r.totalPartnerings, rep: r.repeatPartners, div: r.partnerDiversity },
                          { k: "Opponents", uniq: r.uniqueOpponents, tot: r.totalOpponentings, rep: r.repeatOpponents, div: r.opponentDiversity },
                        ].map((s) => (
                          <div key={s.k}>
                            <div className="flex items-center justify-between text-[11px] mb-1">
                              <span style={{ color: "var(--text-2)" }}>{s.k}</span>
                              <span className="mono text-primary-c">{s.uniq}/{s.tot} uniq{s.rep > 0 ? ` · ${s.rep} rpt` : ""}</span>
                            </div>
                            <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--surface)" }}>
                              <div style={{ width: pct(s.div), height: "100%", background: s.div >= 0.8 ? "#22c55e" : s.div >= 0.5 ? "#eab308" : "#ef4444" }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* edit player (name / level / partner) */}
      <Dialog open={!!editId} onOpenChange={(o) => !o && setEditId(null)}>
        <DialogContent data-testid="player-edit-modal" className="glass" style={{ background: "var(--surface-glass)" }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display"><Pencil className="w-5 h-5 text-primary-c" /> Edit player</DialogTitle>
            <DialogDescription style={{ color: "var(--text-2)" }}>Update the name, skill level or partner.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input data-testid="edit-name-input" value={editName} onChange={(e) => setEditName(e.target.value)}
              placeholder="Player name" className="bg-surface glow-border focus:ring-2 focus:ring-lime-400" />
            <select data-testid="edit-skill-select" value={editSkill} onChange={(e) => setEditSkill(Number(e.target.value))}
              className="w-full rounded-md bg-surface glow-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-lime-400" style={{ color: "var(--text)" }}>
              {SKILLS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
            <div>
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-primary-c mb-1">Partner</div>
              <select data-testid="edit-partner-select" value={editPartner} onChange={(e) => setEditPartner(e.target.value)}
                className="w-full rounded-md bg-surface glow-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-lime-400" style={{ color: "var(--text)" }}>
                <option value="">No partner</option>
                {state.players.filter((x) => x.id !== editId && (!x.partnerId || x.partnerId === editId))
                  .map((x) => <option key={x.id} value={x.id}>{`${x.name} (L${x.skillLevel})`}</option>)}
              </select>
            </div>
            {(() => {
              const pname = getPlayer(state, editId)?.name;
              const matches = (state.history || []).filter((m) => {
                if (Array.isArray(m.team_a_ids) || Array.isArray(m.team_b_ids)) {
                  return [...(m.team_a_ids || []), ...(m.team_b_ids || [])].includes(editId);
                }
                return [...(m.team_a || []), ...(m.team_b || [])].includes(pname);
              });
              return (
                <div data-testid={`match-history-${editId}`}>
                  <div className="text-xs tracking-[0.2em] uppercase font-bold text-primary-c mb-1">Match History ({matches.length})</div>
                  {matches.length === 0 ? (
                    <p className="text-xs" style={{ color: "var(--text-2)" }} data-testid="match-history-empty">No matches played yet.</p>
                  ) : (
                    <div className="max-h-[55vh] overflow-y-auto space-y-1.5 pr-1">
                      {matches.map((m) => {
                        const hasIds = Array.isArray(m.team_a_ids) && Array.isArray(m.team_b_ids);
                        const onA = hasIds ? m.team_a_ids.includes(editId) : (m.team_a || []).includes(pname);
                        const nameOfId = (id) => getPlayer(state, id)?.name || "—";
                        const mine = hasIds ? (onA ? m.team_a_ids : m.team_b_ids).map(nameOfId) : (onA ? m.team_a : m.team_b) || [];
                        const opp = hasIds ? (onA ? m.team_b_ids : m.team_a_ids).map(nameOfId) : (onA ? m.team_b : m.team_a) || [];
                        const partner = hasIds
                          ? (onA ? m.team_a_ids : m.team_b_ids).filter((id) => id !== editId).map(nameOfId)
                          : mine.filter((n) => n !== pname);
                        const decided = !!m.winner;
                        const won = m.winner ? ((m.winner === "A") === onA) : false;
                        const hasScore = m.score_a != null && m.score_b != null;
                        return (
                          <div key={m.id} data-testid="match-history-row" className="text-xs rounded-lg px-2.5 py-2" style={{ background: "var(--bg)" }}>
                            <div className="flex items-center justify-between gap-2">
                              <span className="truncate">
                                {partner.length > 0 && <>w/ <span className="text-primary-c">{partner.join(" & ")}</span> </>}
                                vs {opp.join(" & ") || "—"}
                              </span>
                              <span className="mono text-[10px] font-bold uppercase px-1.5 py-0.5 rounded shrink-0"
                                style={decided ? (won ? { background: "rgba(34,197,94,0.16)", color: "#22c55e" } : { background: "rgba(239,68,68,0.16)", color: "#ef4444" }) : { background: "var(--surface)", color: "var(--text-2)" }}>
                                {decided ? (won ? "Won" : "Lost") : "No result"}
                              </span>
                            </div>
                            {hasScore && (
                              <div className="mono text-[10px] mt-1" style={{ color: "var(--text-2)" }}>
                                {onA ? m.score_a : m.score_b} – {onA ? m.score_b : m.score_a}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <button data-testid="edit-cancel" onClick={() => setEditId(null)} className="glass rounded-full px-4 py-2 font-bold" style={{ color: "var(--text)" }}>Cancel</button>
            <button data-testid="edit-save" onClick={saveEdit} className="btn-glow rounded-full px-5 py-2 font-bold">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* share + recap */}
      <Dialog open={showShare} onOpenChange={setShowShare}>
        <DialogContent data-testid="share-modal" className="glass max-w-lg max-h-[85vh] overflow-y-auto" style={{ background: "var(--surface-glass)" }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Share2 className="w-5 h-5 text-primary-c" /> Share & Recap
            </DialogTitle>
            <DialogDescription style={{ color: "var(--text-2)" }}>
              Players scan to follow the queue, courts and standings live. Doubles as your end-of-day recap.
            </DialogDescription>
          </DialogHeader>

          {shareToken ? (
            <div className="rounded-2xl glow-border p-5 flex flex-col items-center gap-4" style={{ background: "var(--surface)" }}>
              <div className="bg-white p-3 rounded-xl" data-testid="share-qr">
                <QRCodeCanvas value={`${window.location.origin}/live/${shareToken}`} size={168} includeMargin={false} />
              </div>
              <div className="w-full flex items-center gap-2">
                <input readOnly data-testid="share-link" value={`${window.location.origin}/live/${shareToken}`}
                  className="flex-1 rounded-lg bg-surface glow-border px-3 py-2 text-xs mono truncate" style={{ color: "var(--text)" }} />
                <button data-testid="copy-share-link"
                  onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/live/${shareToken}`); toast.success("Link copied"); }}
                  className="btn-glow rounded-lg px-3 py-2 font-bold flex items-center gap-1 shrink-0"><Copy className="w-4 h-4" /></button>
              </div>
              <a href={`${window.location.origin}/live/${shareToken}`} target="_blank" rel="noreferrer"
                data-testid="open-live-view" className="text-sm font-bold text-primary-c hover:underline flex items-center gap-1">
                <QrCode className="w-4 h-4" /> Open live spectator view
              </a>
            </div>
          ) : (
            <div className="rounded-2xl glow-border p-6 text-center text-sm" style={{ background: "var(--surface)", color: "var(--text-2)" }}>
              Generating your share link… (needs an internet connection)
            </div>
          )}

          {/* final standings */}
          <div className="mt-4">
            <h4 className="font-display font-bold flex items-center gap-2 mb-3"><ListOrdered className="w-4 h-4 text-primary-c" /> Final Standings</h4>
            {state.players.length === 0 ? (
              <p className="text-sm" style={{ color: "var(--text-2)" }}>No players yet.</p>
            ) : (
              <div className="space-y-1.5" data-testid="recap-standings">
                {standings(state, rankIncludePaired).map((p, i) => {
                  const podium = PODIUM[i + 1];
                  const m = metricSummary(p, scoringMode);
                  return (
                    <div key={p.id} className="flex items-center justify-between rounded-xl px-3 py-2 text-sm"
                      style={{ background: "var(--bg)", border: podium ? `1px solid ${podium.color}` : "1px solid transparent" }}>
                      <span className="flex items-center gap-3 min-w-0">
                        <span className="w-6 flex justify-center">{podium ? <podium.icon className="w-4 h-4" style={{ color: podium.color }} /> : <span className="mono text-xs" style={{ color: "var(--text-2)" }}>{i + 1}</span>}</span>
                        <span className="min-w-0">
                          <span className="font-semibold flex items-center gap-1.5 min-w-0" style={podium ? { color: podium.color } : {}}><span className="truncate">{p.name}</span><TieInfo note={p.tieNote} id={`recap-${i + 1}`} /></span>
                          <span className="mono text-[11px]" style={{ color: "var(--text-2)" }}>{m.primary} · {m.secondary}</span>
                        </span>
                      </span>
                      <span className="mono text-xs shrink-0"><span className="text-primary-c font-bold">{p.wins || 0}W</span> <span style={{ color: "var(--text-2)" }} data-testid={`live-ranking-games-${i + 1}`}>{(p.wins || 0) + (p.losses || 0)}g</span></span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* match summary */}
          <div className="mt-4">
            <h4 className="font-display font-bold flex items-center gap-2 mb-3"><ScrollText className="w-4 h-4 text-primary-c" /> Matches ({(state.history || []).length})</h4>
            {(state.history || []).length === 0 ? (
              <p className="text-sm" style={{ color: "var(--text-2)" }}>No matches finished yet.</p>
            ) : (
              <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1" data-testid="recap-matches">
                {state.history.map((m) => (
                  <div key={m.id} className="rounded-xl px-3 py-2 text-sm" style={{ background: "var(--bg)" }}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate">{m.team_a.join(" & ")} <span style={{ color: "var(--text-2)" }}>vs</span> {m.team_b.join(" & ")}</span>
                      <span className="mono text-xs text-primary-c shrink-0">{m.winners?.length ? m.winners.join(" & ") : "—"}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* end session confirmation */}
      <Dialog open={showEndConfirm} onOpenChange={setShowEndConfirm}>
        <DialogContent data-testid="end-session-modal" className="glass" style={{ background: "var(--surface-glass)" }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <AlertTriangle className="w-5 h-5" style={{ color: "#dc2626" }} /> End this session?
            </DialogTitle>
            <DialogDescription style={{ color: "var(--text-2)" }}>
              This finalizes the standings and closes the live session. You'll still be able to view the result and share it from your dashboard, but you won't be able to keep staging matches.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button data-testid="end-session-cancel" onClick={() => setShowEndConfirm(false)}
              className="glass rounded-full px-4 py-2 font-bold" style={{ color: "var(--text)" }}>Keep playing</button>
            <button data-testid="end-session-confirm" onClick={confirmEndSession}
              className="rounded-full px-5 py-2 font-bold text-white" style={{ background: "#dc2626" }}>End session</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* final standings after ending */}
      <FinalStandingsModal finals={finalStandings} onClose={() => setFinalStandings(null)} onDashboard={() => { setFinalStandings(null); nav("/dashboard"); }} />

      {/* auto call screen for a side monitor */}
      <CallScreen call={callScreen} onClose={() => setCallScreen(null)}
        onAnnounce={() => callScreen && announce(stageMessage(callScreen.court_name, callScreen.teamAnames, callScreen.teamBnames, callScreen.mode), audioEnabled)} />
    </div>
  );
}

const PODIUM = {
  1: { icon: Crown, color: "#facc15", bg: "rgba(250, 204, 21, 0.10)" },   // gold
  2: { icon: Medal, color: "#cbd5e1", bg: "rgba(203, 213, 225, 0.10)" },  // silver
  3: { icon: Award, color: "#f59e0b", bg: "rgba(245, 158, 11, 0.10)" },   // bronze
};

function fmtSessionDuration(startedAt, endedAt) {
  if (!startedAt || !endedAt || endedAt < startedAt) return "—";
  const totalMin = Math.round((endedAt - startedAt) / 60000);
  if (totalMin < 1) return "<1m";
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function FinalStandingsModal({ finals, onClose, onDashboard }) {
  const rows = finals?.rows || null;
  const mode = finals?.mode || "casual";
  const advanced = mode === "advanced";
  const history = finals?.history || [];
  const participants = finals?.participants || [];
  return (
    <Dialog open={!!finals} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="final-standings-modal" className="glass max-w-lg max-h-[90vh] overflow-y-auto" style={{ background: "var(--surface-glass)" }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Trophy className="w-5 h-5 text-primary-c" /> Final Results
            <span className="ml-auto"><MetricsInfo mode={mode} /></span>
          </DialogTitle>
          <DialogDescription style={{ color: "var(--text-2)" }}>{finals?.eventName || "Session ended"} — here's the full recap.</DialogDescription>
        </DialogHeader>

        {/* summary stats */}
        <div className="grid grid-cols-3 gap-2" data-testid="final-summary">
          {[
            { label: "Matches", value: finals?.matches ?? history.length, icon: ScrollText },
            { label: "Players", value: finals?.players ?? participants.length, icon: Users },
            { label: "Play time", value: fmtSessionDuration(finals?.startedAt, finals?.endedAt), icon: Clock },
          ].map((c) => (
            <div key={c.label} className="rounded-xl px-3 py-2.5 text-center" style={{ background: "var(--bg)" }}>
              <c.icon className="w-4 h-4 mx-auto text-primary-c mb-1" />
              <div className="font-display font-bold text-lg">{c.value}</div>
              <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-2)" }}>{c.label}</div>
            </div>
          ))}
        </div>

        {/* rankings */}
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-primary-c mt-2">Rankings</div>
        {!rows || rows.length === 0 ? (
          <p className="text-sm py-3 text-center" style={{ color: "var(--text-2)" }}>No ranked players in this session.</p>
        ) : (
          <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1" data-testid="final-standings-list">
            {rows.map((p, i) => {
              const rank = i + 1;
              const podium = PODIUM[rank];
              const m = metricSummary(p, mode);
              return (
                <div key={p.id} data-testid={`final-standing-row-${rank}`}
                  className="flex items-center justify-between rounded-xl px-3 py-2.5"
                  style={{ background: podium ? podium.bg : "var(--bg)", border: podium ? `1px solid ${podium.color}` : "1px solid transparent" }}>
                  <span className="flex items-center gap-3 min-w-0">
                    <span className="w-7 flex items-center justify-center shrink-0">
                      {podium ? <podium.icon className="w-4 h-4" style={{ color: podium.color }} /> : <span className="mono text-xs" style={{ color: "var(--text-2)" }}>{rank}</span>}
                    </span>
                    <span className="min-w-0">
                      <span className="truncate font-semibold flex items-center gap-2" style={podium ? { color: podium.color } : {}}>
                        {p.name}
                        <TieInfo note={p.tieNote} id={`final-${rank}`} />
                        {m.tag && <span className="mono text-[9px] uppercase tracking-wider glass rounded-full px-1.5 py-0.5 text-primary-c shrink-0">{m.tag}</span>}
                      </span>
                      <span className="mono text-[11px]" style={{ color: "var(--text-2)" }}>{m.primary} · {m.secondary}</span>
                    </span>
                  </span>
                  <span className="flex items-center gap-4 mono text-sm shrink-0">
                    <span className="font-bold" style={podium ? { color: podium.color } : { color: "var(--text)" }}>{p.wins || 0}W</span>
                    <span style={{ color: "var(--text-2)" }} data-testid={`ranking-games-${rank}`}>{(p.wins || 0) + (p.losses || 0)}g</span>
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* match log */}
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-primary-c mt-3">Match Log ({history.length})</div>
        {history.length === 0 ? (
          <p className="text-sm py-3 text-center" style={{ color: "var(--text-2)" }}>No matches were played.</p>
        ) : (
          <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1" data-testid="final-match-log">
            {history.map((m) => {
              const winA = m.winner === "A";
              const winB = m.winner === "B";
              const hasScore = advanced && m.score_a != null && m.score_b != null;
              return (
                <div key={m.id} data-testid="final-match-row" className="rounded-xl px-3 py-2 text-sm" style={{ background: "var(--bg)" }}>
                  <div className="flex items-center justify-between gap-2">
                    <span className={`truncate ${winA ? "font-bold text-primary-c" : ""}`}>{(m.team_a || []).join(" & ")}</span>
                    <span className="mono text-xs shrink-0" style={{ color: "var(--text-2)" }}>{hasScore ? `${m.score_a} – ${m.score_b}` : "vs"}</span>
                    <span className={`truncate text-right ${winB ? "font-bold text-primary-c" : ""}`}>{(m.team_b || []).join(" & ")}</span>
                  </div>
                  <div className="mono text-[10px] mt-1" style={{ color: "var(--text-2)" }}>
                    {m.court_name ? `${m.court_name} · ` : ""}
                    {m.winners?.length ? `Winner: ${m.winners.join(" & ")}` : "No result"}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* participants */}
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-primary-c mt-3">Players ({participants.length})</div>
        {participants.length === 0 ? (
          <p className="text-sm py-2" style={{ color: "var(--text-2)" }}>No players checked in.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5" data-testid="final-participants">
            {participants.map((p, i) => (
              <span key={i} data-testid="final-participant" className="mono text-[11px] rounded-full px-2 py-1"
                style={p.checkedOut ? { background: "rgba(234,179,8,0.14)", color: "#eab308" } : { background: "var(--bg)", color: "var(--text)" }}>
                {p.name}{p.checkedOut ? " (out)" : ""}
              </span>
            ))}
          </div>
        )}

        <DialogFooter className="mt-2">
          <button data-testid="final-standings-new" onClick={onClose}
            className="glass rounded-full px-4 py-2 font-bold" style={{ color: "var(--text)" }}>Start new session</button>
          <button data-testid="final-standings-dashboard" onClick={onDashboard}
            className="btn-glow rounded-full px-5 py-2 font-bold">Back to dashboard</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NextTeam({ court, ids, offset, state, onRemove, available, onFill }) {
  const slug = court.name.replace(/\s+/g, "-").toLowerCase();
  return (
    <div className="space-y-2 min-w-0">
      {ids.map((id, i) => {
        const idx = offset + i;
        const p = id ? state.players.find((x) => x.id === id) : null;
        if (p) {
          return (
            <div key={idx} data-testid={`nextup-slot-${slug}-${idx}`}
              className="flex items-center justify-between gap-2 rounded-xl px-3 py-3 min-w-0" style={{ background: "var(--bg)" }}>
              <span className="font-semibold text-sm sm:text-base min-w-0 break-words [overflow-wrap:anywhere] leading-tight">
                {p.name} <span className="mono text-xs text-primary-c">L{p.skillLevel}</span>
              </span>
              <button data-testid={`nextup-remove-${slug}-${idx}`} onClick={() => onRemove(court.id, idx)}
                aria-label="Remove player" className="shrink-0 hover:scale-110 transition-transform">
                <X className="w-4 h-4" style={{ color: "var(--text-2)" }} />
              </button>
            </div>
          );
        }
        return (
          <Popover key={idx}>
            <PopoverTrigger asChild>
              <button data-testid={`nextup-slot-${slug}-${idx}`}
                className="w-full flex items-center gap-2 rounded-xl px-3 py-3 border-2 border-dashed hover:border-solid transition-colors"
                style={{ borderColor: "var(--border)", color: "var(--text-2)" }}>
                <Plus className="w-4 h-4 text-primary-c" /> <span className="text-sm italic">Tap to add player</span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="glass w-56 p-2" style={{ background: "var(--surface-glass)" }} data-testid={`slot-picker-${slug}-${idx}`}>
              {available.length === 0 ? (
                <p className="text-sm p-2 text-center" style={{ color: "var(--text-2)" }}>No available players.</p>
              ) : (
                <div className="max-h-56 overflow-y-auto space-y-1">
                  {available.map((ap) => (
                    <button key={ap.id} data-testid={`slot-pick-${ap.id}`} onClick={() => onFill(court.id, idx, ap.id)}
                      className="w-full text-left rounded-lg px-3 py-2 text-sm font-medium hover:bg-black/10 dark:hover:bg-white/5 transition-colors flex items-center justify-between">
                      <span className="truncate">{ap.name}</span>
                      <span className="mono text-xs text-primary-c shrink-0">L{ap.skillLevel}</span>
                    </button>
                  ))}
                </div>
              )}
            </PopoverContent>
          </Popover>
        );
      })}
    </div>
  );
}

function Counter({ label, value, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl p-4" style={{ background: "var(--bg)" }}>
      <Icon className="w-4 h-4 text-primary-c" />
      <div className="font-display font-black text-2xl tracking-tighter mt-1">{value}</div>
      <div className="text-xs" style={{ color: "var(--text-2)" }}>{label}</div>
    </div>
  );
}

function TeamRow({ ids, state, label }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-primary-c mb-1">{label}</div>
      <div className="space-y-1">
        {ids.map((id) => {
          const p = getPlayer(state, id);
          return <div key={id} className="text-sm font-medium truncate">{p?.name || "—"}</div>;
        })}
      </div>
    </div>
  );
}

function CourtCard({ court, state, now, onStage, onFinish, onRecall, onRemove, onStop, onEdit, onRename }) {
  const active = court.status === "in_use" && court.currentMatch;
  const elapsed = active && court.startTime ? (now - court.startTime) / 1000 : 0;
  const canEdit = active && elapsed < 60;
  const editLeft = Math.max(0, 60 - Math.floor(elapsed));
  const slug = court.name.replace(/\s+/g, "-").toLowerCase();
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(court.name);

  const saveName = () => {
    const ok = onRename ? onRename(nameDraft) : false;
    if (ok !== false) setRenaming(false);
  };

  return (
    <motion.div layout data-testid={`court-card-${slug}`}
      className={`relative rounded-2xl p-5 min-h-[220px] flex flex-col ${active ? "tracing-beam glow-border" : "glow-border"}`}
      style={{ background: "var(--surface)" }}>
      <div className="flex items-center justify-between gap-2">
        {renaming ? (
          <span className="flex items-center gap-1.5 flex-1 min-w-0">
            <input data-testid={`court-name-input-${slug}`} autoFocus value={nameDraft} maxLength={40}
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") { setNameDraft(court.name); setRenaming(false); } }}
              className="flex-1 min-w-0 rounded-md bg-surface glow-border px-2 py-1 text-sm font-bold outline-none focus:ring-2 focus:ring-lime-400" style={{ color: "var(--text)" }} />
            <button data-testid={`court-name-save-${slug}`} onClick={saveName} aria-label="Save court name" className="p-1 hover:scale-110 transition-transform"><CheckCircle2 className="w-4 h-4 text-primary-c" /></button>
          </span>
        ) : (
          <button data-testid={`court-rename-${slug}`} onClick={() => { setNameDraft(court.name); setRenaming(true); }}
            className="font-display font-bold flex items-center gap-1.5 hover:text-primary-c transition-colors group min-w-0">
            <span className="truncate">{court.name}</span>
            <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" style={{ color: "var(--text-2)" }} />
          </button>
        )}
        <span className={`text-[10px] tracking-[0.2em] uppercase font-bold px-2 py-0.5 rounded-full shrink-0`}
          style={{ color: active ? "var(--bg)" : "var(--text-2)", background: active ? "var(--primary)" : "var(--bg)" }}>
          {active ? "In use" : "Available"}
        </span>
      </div>

      {active ? (
        <>
          <div className="flex items-center justify-center my-3">
            <span className="mono font-bold text-3xl tracking-tight text-primary-c" data-testid={`court-timer-${slug}`}>{mmss(elapsed)}</span>
          </div>
          <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 flex-1">
            <TeamRow ids={court.currentMatch.teamA} state={state} label="Team A" />
            <span className="mono text-xs" style={{ color: "var(--text-2)" }}>VS</span>
            <TeamRow ids={court.currentMatch.teamB} state={state} label="Team B" />
          </div>
          <div className="flex gap-2 mt-3">
            <button data-testid={`finish-match-button-${slug}`} onClick={onFinish}
              className="flex-1 rounded-full py-2.5 font-bold glass hover:scale-[1.02] transition-transform flex items-center justify-center gap-2" style={{ color: "var(--text)" }}>
              <CheckCircle2 className="w-4 h-4 text-primary-c" /> Finish
            </button>
            <button data-testid={`recall-button-${slug}`} onClick={onRecall}
              aria-label="Recall announcement" className="glass rounded-full w-11 flex items-center justify-center hover:scale-105 transition-transform">
              <Volume2 className="w-4 h-4 text-primary-c" />
            </button>
            <button data-testid={`stop-match-button-${slug}`} onClick={onStop}
              aria-label="Stop game (no result)" className="glass rounded-full w-11 flex items-center justify-center hover:scale-105 transition-transform">
              <Ban className="w-4 h-4" style={{ color: "var(--text-2)" }} />
            </button>
          </div>
          {canEdit && (
            <button data-testid={`edit-lineup-button-${slug}`} onClick={onEdit}
              className="mt-2 w-full rounded-full py-2 text-xs font-bold glass flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform" style={{ color: "var(--text)" }}>
              <Pencil className="w-3.5 h-3.5 text-primary-c" /> Edit lineup · {editLeft}s left
            </button>
          )}
        </>
      ) : (
        <>
          <div className="flex-1 flex flex-col items-center justify-center gap-2 py-6" style={{ color: "var(--text-2)" }}>
            <Timer className="w-8 h-8 opacity-40" />
            <span className="text-sm">Court idle</span>
          </div>
          <div className="flex gap-2">
            <button data-testid={`stage-match-button-${slug}`} onClick={onStage}
              className="flex-1 btn-glow rounded-full py-2.5 font-bold flex items-center justify-center gap-2">
              <Play className="w-4 h-4" /> Stage Next <ChevronRight className="w-4 h-4" />
            </button>
            <button data-testid={`remove-court-${slug}`} onClick={onRemove}
              aria-label="Remove court" className="glass rounded-full w-11 flex items-center justify-center hover:scale-105 transition-transform">
              <Trash2 className="w-4 h-4" style={{ color: "var(--text-2)" }} />
            </button>
          </div>
        </>
      )}
    </motion.div>
  );
}
